// src/pages/CRM/SFA/ForecastDashboard.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Button,
  Divider, LinearProgress, Alert, Chip, Stack, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tab, Tabs, Avatar, useTheme
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Flag as FlagIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  PieChart as PieChartIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../utils/permissionUtils';
import {
  formatCurrency,
  formatPercent,
  ForecastCategoryLabels,
  ForecastCategoryColors,
  TargetPeriodLabels
} from './sfaEnums';
import { forecastService, salesTargetService } from './sfaService';


// Simple chart components using CSS (can be replaced with recharts/chart.js if installed)
const ProgressBar = ({ value, max, color = 'primary', label }) => {
  const percent = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="caption">{formatPercent(percent)}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={color}
        sx={{ height: 10, borderRadius: 5 }}
      />
    </Box>
  );
};

const BarChart = ({ data, valueKey, labelKey, color = '#1976d2', height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey] || 0), 1);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height, gap: 1 }}>
      {data.map((item, index) => {
        const barHeight = (item[valueKey] / maxValue) * (height - 40);
        return (
          <Tooltip key={index} title={`${item[labelKey]}: ${formatCurrency(item[valueKey])}`}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: Math.max(barHeight, 4),
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  transition: 'height 0.3s ease'
                }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, fontSize: 10, textAlign: 'center' }}>
                {item[labelKey]}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const ForecastDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('QUARTERLY');
  const [tabValue, setTabValue] = useState(0);
  const [forecastSummary, setForecastSummary] = useState(null);
  const [forecastByPeriod, setForecastByPeriod] = useState([]);
  const [forecastByCategory, setForecastByCategory] = useState(null);
  const [salesTargets, setSalesTargets] = useState([]);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPerms = async () => {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    };
    checkPerms();
  }, []);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { chatbot_id: chatbotId, period_type: periodType };

      const [summaryRes, periodRes, categoryRes, targetsRes] = await Promise.all([
        forecastService.getSummary(params),
        forecastService.getByPeriod({ ...params, months_ahead: 6 }),
        forecastService.getByCategory(params),
        salesTargetService.getAll({ chatbot_id: chatbotId, period_type: periodType })
      ]);

      if (summaryRes.success) setForecastSummary(summaryRes.data);
      if (periodRes.success) setForecastByPeriod(periodRes.data || []);
      if (categoryRes.success) setForecastByCategory(categoryRes.data);
      if (targetsRes.success) setSalesTargets(targetsRes.data || []);
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      enqueueSnackbar('Failed to load forecast data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [canView, chatbotId, periodType]);

  const handleRecalculateActuals = async () => {
    try {
      await salesTargetService.recalculateActuals();
      enqueueSnackbar('Actuals recalculated successfully', { variant: 'success' });
      fetchData();
    } catch (error) {
      enqueueSnackbar('Failed to recalculate actuals', { variant: 'error' });
    }
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view forecasting.
      </Alert>
    );
  }

  const summary = forecastSummary?.summary || {};

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Sales Forecast
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revenue predictions and quota tracking
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              label="Period"
            >
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
              <MenuItem value="YEARLY">Yearly</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
          {canModify && (
            <Button
              variant="contained"
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/targets`)}
            >
              Manage Targets
            </Button>
          )}
        </Stack>
      </Box>

      {loading ? (
        <LinearProgress sx={{ mb: 3 }} />
      ) : (
        <>
          {/* Period Info */}
          {forecastSummary?.period && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Showing forecast for {TargetPeriodLabels[periodType] || periodType} period:&nbsp;
              <strong>{new Date(forecastSummary.period.start).toLocaleDateString()}</strong> to&nbsp;
              <strong>{new Date(forecastSummary.period.end).toLocaleDateString()}</strong>
            </Alert>
          )}

          {/* Modern Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Total Pipeline
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(summary.total_pipeline || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Open opportunities
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                      <TrendingUpIcon />
                    </Avatar>
                  </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Weighted Forecast
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(summary.total_weighted || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Probability-adjusted
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                      <AssessmentIcon />
                    </Avatar>
                  </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Best Case
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(summary.best_case || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Optimistic forecast
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                      <ArrowUpIcon />
                    </Avatar>
                  </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Commit
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(summary.commit || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Committed deals
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                      <FlagIcon />
                    </Avatar>
                  </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Closed Won
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(summary.closed || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        This period
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                      <CheckCircleIcon />
                    </Avatar>
                  </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #536976 0%, #292E49 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Target
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(summary.target || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Quota for period
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                      <CalendarIcon />
                    </Avatar>
                  </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Card>
            </Grid>
          </Grid>

          {/* Gap to Target */}
          {summary.target > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Target Progress
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <ProgressBar
                    value={summary.closed || 0}
                    max={summary.target || 0}
                    color={(summary.achievement_percent || 0) >= 100 ? 'success' : 'primary'}
                    label={`Closed: ${formatCurrency(summary.closed || 0)} / Target: ${formatCurrency(summary.target || 0)}`}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: (summary.gap_to_target || 0) <= 0 ? 'success.50' : 'warning.50', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Gap to Target</Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color={(summary.gap_to_target || 0) <= 0 ? 'success.main' : 'warning.main'}
                    >
                      {(summary.gap_to_target || 0) <= 0 ? (
                        <>
                          <ArrowUpIcon sx={{ verticalAlign: 'middle' }} />
                          +{formatCurrency(Math.abs(summary.gap_to_target || 0))}
                        </>
                      ) : (
                        <>
                          <ArrowDownIcon sx={{ verticalAlign: 'middle' }} />
                          {formatCurrency(summary.gap_to_target || 0)}
                        </>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(summary.gap_to_target || 0) <= 0 ? 'Above target!' : 'Needed to hit quota'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Tabs for different views */}
          <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, v) => setTabValue(v)} 
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                bgcolor: 'grey.50',
                '& .MuiTab-root': { fontWeight: 600, minHeight: 56, textTransform: 'none' },
                '& .Mui-selected': { bgcolor: 'white' }
              }}
            >
              <Tab label="By Category" icon={<BarChartIcon />} iconPosition="start" />
              <Tab label="By Period" icon={<TimelineIcon />} iconPosition="start" />
              <Tab label="Sales Targets" icon={<FlagIcon />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* By Category Tab */}
              {tabValue === 0 && (
                <Box sx={{ p: 3, mx: 'auto' }}>
                  <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
                    Forecast by Category
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  {forecastSummary?.forecast_categories ? (
                    <Grid container spacing={3}>
                      {Object.entries(forecastSummary.forecast_categories).map(([category, data]) => (
                        <Grid item xs={12} sm={6} md={3} key={category}>
                          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                            <CardContent>
                              <Chip
                                label={ForecastCategoryLabels[category] || category}
                                size="small"
                                sx={{
                                  mb: 1.5,
                                  bgcolor: ForecastCategoryColors[category] || 'grey.500',
                                  color: 'white'
                                }}
                              />
                              <Typography variant="h5" fontWeight={700}>
                                {formatCurrency(data.amount)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {data.count} deal{data.count !== 1 ? 's' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Weighted: {formatCurrency(data.weighted)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <PieChartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">No category data available</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* By Period Tab */}
              {tabValue === 1 && (
                <Box sx={{ p: 3, mx: 'auto' }}>
                  <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
                    Forecast by Period (Next 6 Months)
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  {forecastByPeriod.length > 0 ? (
                    <>
                      <Box sx={{ height: 250, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <BarChart
                          data={forecastByPeriod}
                          valueKey="total_forecast"
                          labelKey="month_name"
                          color="#1976d2"
                          height={230}
                        />
                      </Box>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Open Deals</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Open Amount</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Weighted</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Won</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Total Forecast</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {forecastByPeriod.map((period, index) => (
                              <TableRow key={index} hover>
                                <TableCell>{period.month_name}</TableCell>
                                <TableCell align="right">{period.open_count}</TableCell>
                                <TableCell align="right">{formatCurrency(period.open_amount)}</TableCell>
                                <TableCell align="right">{formatCurrency(period.weighted_amount)}</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                                  {formatCurrency(period.won_amount)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(period.total_forecast)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">No period data available</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Sales Targets Tab */}
              {tabValue === 2 && (
                <Box sx={{ p: 3, mx: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={600}>Sales Targets</Typography>
                    {canModify && (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={handleRecalculateActuals}>
                          Recalculate Actuals
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate(`/chatbot/${chatbotId}/crm/targets`)}
                        >
                          Manage Targets
                        </Button>
                      </Stack>
                    )}
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  {salesTargets.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Territory / Family</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Target</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actual</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Gap</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Achievement</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {salesTargets.map((target) => (
                            <TableRow key={target.id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(target.period_start).toLocaleDateString()} -{' '}
                                  {new Date(target.period_end).toLocaleDateString()}
                                </Typography>
                                <Chip label={target.period_type} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                              </TableCell>
                              <TableCell>
                                {target.territory || target.product_family || 'All'}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(target.target_amount)}</TableCell>
                              <TableCell align="right">{formatCurrency(target.actual_amount)}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={500}
                                  color={target.gap <= 0 ? 'success.main' : 'error.main'}
                                >
                                  {target.gap <= 0 ? '+' : '-'}{formatCurrency(Math.abs(target.gap))}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(target.achievement_percent, 100)}
                                    color={target.achievement_percent >= 100 ? 'success' : target.achievement_percent >= 75 ? 'primary' : 'warning'}
                                    sx={{ width: 60, height: 6, borderRadius: 3 }}
                                  />
                                  <Typography variant="body2" fontWeight={500}>
                                    {formatPercent(target.achievement_percent)}%
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <FlagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary" gutterBottom>
                        No sales targets defined
                      </Typography>
                      {canModify && (
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/chatbot/${chatbotId}/crm/targets`)}
                          sx={{ mt: 2 }}
                        >
                          Create Target
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>

          {/* Category Breakdown */}
          {forecastByCategory?.categories && forecastByCategory.categories.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Pipeline by {forecastByCategory.group_by === 'stage' ? 'Stage' : forecastByCategory.group_by === 'owner' ? 'Owner' : 'Category'}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {forecastByCategory.categories.map((cat, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>{cat.name}</Typography>
                        <Typography variant="h6" fontWeight={700}>{formatCurrency(cat.amount)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cat.count} deal{cat.count !== 1 ? 's' : ''} • Weighted: {formatCurrency(cat.weighted)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default ForecastDashboard;